import React, { Component, ReactNode } from 'react';
import { INSERT_SLIDE_ORIGIN_TYPE, KEY } from '@src/js/constant';
import DUISnackbar from '@tencent/dui/lib/components/Snackbar';
import ColorPicker from '../color-picker';
import i18n from '@i18next/i18n';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IRightMenuManager } from '@src/slide/application/service/right-menu-manager/core/right-menu-manager.interface';
import { IMenuItems } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { ITransitionService } from '@src/slide/application/service/transition-service/transition-service-interface';

export const PopupMenuType = {
    SLIDE: 'slide',
    ELEMENT: 'element',
    SLIDE_GAP: 'slide_gap',
    NOTE: 'note',
    LAYOUT_MASTER: 'layout-master',
    LAYOUT_MASTER_GAP: 'layout-master-gap',
};

let rightMenuManager: IRightMenuManager | null = null;
let transitionService: ITransitionService | null = null;

interface PopupWrapperProps {
    type: string; // PopupMenuType
    index?: number;
    sectionIndex?: number;
    style?: React.CSSProperties;
    children?: ReactNode;
}

interface PopupWrapperState {
    isLoading: boolean;
}

class PopupWrapper extends Component<PopupWrapperProps, PopupWrapperState> {
    // 这个 map 提取了部分菜单项，后续可以补充全部菜单项
    private showMenuMap = {
        [PopupMenuType.SLIDE]: () => {
            if (ColorPicker.isShown()) {
                return;
            }
            const menuData = this.getMenuManager()?.rightMenu(true);
            const regionType = RegionType.SLIDELIST;
            return { menuData, regionType };
        },
        [PopupMenuType.LAYOUT_MASTER]: () => {
            const menuData = this.getMenuManager()?.rightMenu4Layout();
            const regionType = RegionType.SLIDELIST;
            return { menuData, regionType };
        },
        [PopupMenuType.LAYOUT_MASTER_GAP]: () => {
            const menuData = this.getMenuManager()?.rightMenu4SlideGap(this.props.index!, true);
            const regionType = RegionType.SLIDELIST;
            return { menuData, regionType };
        },
    };

    public constructor(props: PopupWrapperProps) {
        super(props);
    }

    public componentDidMount() {
        this.getAppService().viewableServiceLayer.
            rightMenuManager.promise().then((rightMenuManagerInService) => {
                rightMenuManager = rightMenuManagerInService;
            });

        this.getAppService().editableServiceLayer?.transitionService?.promise().then((module) => {
            transitionService = module;
        });
    }

    public render() {
        const style = {
            width: '100%',
            height: '100%',
            ...this.props.style,
            // position: 'absolute', // IE 10上会导致右侧编辑区的原点出错，先去掉
        };

        return (
            <div id={'PopupWrapper'} style={style} onContextMenu={this.handleContextMenu.bind(this)}>
                {this.props.children}
            </div>
        );
    }

    /**
     * 检查弹出的位置是否有offset偏移，这里主要是为了处理列表iframe渲染的情况
     */
    // private checkOffset = (e: React.MouseEvent<HTMLDivElement>) => {
    //     const inSlideList = this.props.type === PopupMenuType.SLIDE
    //         || this.props.type === PopupMenuType.SLIDE_GAP;
    //     const inLayoutList = this.props.type === PopupMenuType.LAYOUT_MASTER
    //         || this.props.type === PopupMenuType.LAYOUT_MASTER_GAP;
    //     let offsetY = 0;
    //     // @ts-ignore
    //     const isInIframe = !(e.view === window);
    //     const iframeEditKey = inSlideList ? EDIT_TYPE.SLIDE : EDIT_TYPE.MASTER_LAYOUT;
    //     if ((inSlideList || inLayoutList) && isInIframe) {
    //         const iframe = document.querySelectorAll(`iframe[id*=${KEY.EDITOR_FRAME}-${iframeEditKey}]`)[0];
    //         if (iframe) {
    //             const scrollView = iframe.parentNode;
    //             if (scrollView) {
    //                 const rect = scrollView.getBoundingClientRect();
    //                 offsetY = rect.top - scrollView.scrollTop;
    //             }
    //         }
    //     }
    //     return { offsetX: 0, offsetY };
    // };

    private ifShowLoading = () => (
        (this.props.type === PopupMenuType.SLIDE || (this.props.type === PopupMenuType.SLIDE_GAP))
        && this.getAppService()
            .getUiDataService().presentation.operation.loading === true
    );

    private getMenuManager = () => this.getAppService().editableServiceLayer?.menuManager;

    private getLinkFloatBarService = () => this.getAppService().viewableServiceLayer.linkManager.linkFloatBarService;

    private isTransitionPreviewing = () => !!transitionService?.getPreviewState();

    // eslint-disable-next-line complexity
    private handleContextMenu(e: React.MouseEvent<HTMLDivElement>) {
        e.stopPropagation();
        e.preventDefault();
        // const offsetObj = this.checkOffset(e);  // 检查是否有偏移

        if (this.ifShowLoading()) {
            DUISnackbar.show({
                message: i18n.t('正在加载幻灯片，请稍后再试'),
                type: 'info',
                autoClose: true,
            });
            return;
        }

        // 如果处于切页动画的预览，直接停止预览
        if (this.isTransitionPreviewing()) {
            transitionService?.stopPreview();
            return;
        }

        let menuData: IMenuItems | undefined;
        let regionType;
        // 显示右键菜单
        const { menuData: newMenuData, regionType: newRegionType } = this.showMenuMap[this.props.type]?.() ?? {};
        menuData = newMenuData;
        regionType = newRegionType;
        if (this.props.type === PopupMenuType.ELEMENT) {
            if (ColorPicker.isShown()) {
                return;
            }
            menuData = this.getMenuManager()?.rightMenu();
            regionType = RegionType.EDITOR;
        } else if (this.props.type === PopupMenuType.SLIDE_GAP) {
            const disableIds = this.getMenuManager()?.disableItemIds();
            const menuData = this.getMenuManager()?.rightMenu4SlideGap(this.props.index!);

            menuData && this.handleShowRightMenu(menuData.type!, menuData.items!, disableIds!, {
                top: e.clientY,
                left: e.clientX + 8,
                minWidth: 200,
                subMinWidth: 200,
                onSubMenuVisibleChange: this.onSubMenuVisibleChange.bind(this, menuData),
            }, (menuType, itemID, object) => {
                menuData.callback(menuType, itemID, {
                    RegionType: RegionType.SLIDELIST,
                    gapIndex: this.props.index,
                    sectionIndex: this.props.sectionIndex,
                    TriggerFrom: INSERT_SLIDE_ORIGIN_TYPE.LEFT,
                    object,
                });
            });
        } else if (this.props.type === PopupMenuType.NOTE) {
            // TODO
            menuData = this.getMenuManager()?.rightMenu();
            regionType = RegionType.EDITOR;
        } else {
            // Do noting.
        }

        if (menuData) {
            this.handleDisableHideAllRightMenu(); // 不让hide右键菜单
            this.getMenuManager()?.hideAllMenu();
            const disableIds = this.getMenuManager()?.disableItemIds();
            // 此处如果有工具栏，工具栏需要隐藏 并且给工具栏设置标识位 不允许显示
            if (this.getLinkFloatBarService().isShown()) {
                this.getLinkFloatBarService().hide();
            }

            this.handleShowRightMenu(menuData.type!, menuData.items!, disableIds!, {
                top: e.clientY,
                left: e.clientX + 8,
                minWidth: 200,
                subMinWidth: 200,
                onVisibleChange: this.onVisibleChange,
                onSubMenuVisibleChange: this.onSubMenuVisibleChange.bind(this, menuData),
            }, (menuType, itemID, object) => {
                menuData?.callback(menuType, itemID, {
                    popupMenuType: this.props.type,
                    RegionType: regionType,
                    TriggerFrom: INSERT_SLIDE_ORIGIN_TYPE.LEFT,
                    object,
                    gapIndex: this.props.index,
                });
            });
        }
    }

    private handleShowRightMenu(
        type: string, items: any[],
        disabledIds: Array<{ id: string }>, props: any, callback: any,
    ) {
        if (rightMenuManager) {
            rightMenuManager.showMenu(type, items, disabledIds, props, (...args: any[]) => {
                rightMenuManager!.hideMenu(type);
                callback(...args);
            });
        }
    }

    private handleDisableHideAllRightMenu() {
        if (rightMenuManager) {
            rightMenuManager.disableHideAll();
        }
    }

    private onVisibleChange = (visible: boolean) => {
        const menuManager = this.getMenuManager();
        if (menuManager) {
            menuManager.isMenuShown = visible;
        }
    };

    // 子菜单【显示/隐藏】
    private onSubMenuVisibleChange = (menuData: any, visible: boolean, id: string) => {
        menuData.callback(MenuType.POP_MENU, id, {
            TriggerFrom: INSERT_SLIDE_ORIGIN_TYPE.LEFT,
            visible,
        });
    };

    private getAppService = () => AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
}

export default PopupWrapper;
